import * as React from 'react';
import { FocusEvent, useState, useEffect, ChangeEventHandler, FocusEventHandler, ChangeEvent } from 'react';

import { InputLabelBaseComponent } from '../../theme';
import { UseState } from '../../types';

import { BaseTextarea, CharsCounter, Container, InputContainer } from './textarea.styled';
import { TextareaProps } from './textarea.types';

export const Textarea: React.FC<TextareaProps> = (props: TextareaProps): JSX.Element => {
  const {
    value,
    label,
    disabled = false,
    onChange,
    onBlur,
    maxCharsCount,
    className
  }: TextareaProps = props;
  const [isLabelOnTop, setIsLabelOnTop]: UseState<boolean> = useState<boolean>(!!value);
  const [textareaValue, setTextareaValue]: UseState<string | undefined> = useState<string | undefined>(value);
  const [charsCounter, setCharsCounter]: UseState<number> = useState<number>(value?.length ?? 0);

  const updateValueAndCharsCounter: (updatedValue?: string) => void = (updatedValue?: string): void => {
    setIsLabelOnTop(!!updatedValue);
    setTextareaValue(updatedValue);
    setCharsCounter(updatedValue?.length ?? 0);
  };

  useEffect((): void => updateValueAndCharsCounter(value), [value]);

  const onChangeAction: ChangeEventHandler<HTMLTextAreaElement> = (e: ChangeEvent<HTMLTextAreaElement>): void => {
    const valueFromEvent: string = e.target.value;
    if (maxCharsCount && valueFromEvent.length > maxCharsCount) {
      return;
    }

    if (onChange) {
      onChange(valueFromEvent);
    }
    updateValueAndCharsCounter(valueFromEvent);
  };

  const onBlurAction: FocusEventHandler<HTMLTextAreaElement> = (e: FocusEvent<HTMLTextAreaElement, Element>): void => {
    const valueFromEvent: string = e.target.value;
    if (maxCharsCount && valueFromEvent.length > maxCharsCount) {
      return;
    }

    if (onBlur) {
      onBlur(valueFromEvent);
    }
    setIsLabelOnTop(!!valueFromEvent);
  };

  const onFocusAction: FocusEventHandler<HTMLTextAreaElement> = (): void => setIsLabelOnTop(true);

  return (
    <Container className={className}>
      <InputContainer disabled={disabled} $isCharsCounterActive={!!maxCharsCount}>
        {label && (
          <InputLabelBaseComponent
            $isOnTop={isLabelOnTop}
            $disabled={disabled}
            data-testid='label'
          >
            {label}
          </InputLabelBaseComponent>
        )}
        <BaseTextarea
          disabled={disabled}
          onChange={onChangeAction}
          onBlur={onBlurAction}
          onFocus={onFocusAction}
          maxLength={maxCharsCount}
          data-testid='textarea'
          value={textareaValue}
        >{textareaValue}</BaseTextarea>
        {!!maxCharsCount && (
          <CharsCounter data-testid='counter' >
            {charsCounter} | {maxCharsCount}
          </CharsCounter>
        )}
      </InputContainer>
    </Container>
  );
};
